import { useState, useEffect } from "react";
import QRCode from "qrcode";
import { Panel, Label } from "@/components/SplitLayout";
import { DownloadSimple } from "@phosphor-icons/react";

export default function QrCodeGenerator() {
  const [text, setText] = useState("https://experttechnologies.com");
  const [fg, setFg] = useState("#09090B");
  const [bg, setBg] = useState("#FFFFFF");
  const [size, setSize] = useState(320);
  const [dataUrl, setDataUrl] = useState("");

  useEffect(() => {
    if (!text.trim()) {
      setDataUrl("");
      return;
    }
    QRCode.toDataURL(text, {
      width: size,
      margin: 2,
      color: { dark: fg, light: bg },
      errorCorrectionLevel: "M",
    })
      .then(setDataUrl)
      .catch(() => setDataUrl(""));
  }, [text, fg, bg, size]);

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "qrcode.png";
    a.click();
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-6">
        <Panel title="Content">
          <div className="space-y-5">
            <div className="space-y-2">
              <Label>Text or URL</Label>
              <textarea
                data-testid="qr-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter a URL or any text…"
                className="h-28 w-full resize-none border border-border bg-card p-4 text-sm outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Foreground</Label>
                <input
                  data-testid="qr-fg"
                  type="color"
                  value={fg}
                  onChange={(e) => setFg(e.target.value)}
                  className="h-11 w-full cursor-pointer border border-border bg-card"
                />
              </div>
              <div className="space-y-2">
                <Label>Background</Label>
                <input
                  data-testid="qr-bg"
                  type="color"
                  value={bg}
                  onChange={(e) => setBg(e.target.value)}
                  className="h-11 w-full cursor-pointer border border-border bg-card"
                />
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Size</Label>
                <span className="font-mono text-sm font-bold text-primary">{size}px</span>
              </div>
              <input
                data-testid="qr-size"
                type="range"
                min={128}
                max={640}
                step={32}
                value={size}
                onChange={(e) => setSize(+e.target.value)}
                className="w-full accent-[hsl(var(--primary))]"
              />
            </div>
          </div>
        </Panel>
      </div>

      <div className="lg:sticky lg:top-24 lg:self-start">
        <Panel
          title="Preview"
          testId="qr-output"
          action={
            dataUrl ? (
              <button
                data-testid="qr-download"
                onClick={download}
                className="inline-flex items-center gap-2 border border-border bg-card px-3.5 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-all duration-150 hover:-translate-y-0.5 hover:border-primary hover:text-primary"
              >
                <DownloadSimple size={15} weight="bold" /> PNG
              </button>
            ) : null
          }
        >
          <div className="grid min-h-[320px] place-items-center bg-background p-6">
            {dataUrl ? (
              <img
                data-testid="qr-image"
                src={dataUrl}
                alt="Generated QR code"
                className="max-w-full border border-border"
              />
            ) : (
              <span className="text-sm text-muted-foreground">
                Enter text to generate a QR code
              </span>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}
